import { Link } from "react-router-dom";
import { useEffect } from "react";

interface BlogPost {
  slug: string;
  title: string;
  description: string;
  date: string;
  readTime: string;
  category: string;
}

const BLOG_POSTS: BlogPost[] = [
  {
    slug: "echonour-vs-rev",
    title: "Echonour vs Rev: An Honest Comparison for 2026",
    description: "Comparing Echonour and Rev for transcription — pricing, accuracy, speaker identification, and when to choose each. A fair look at two different approaches.",
    date: "2026-02-23",
    readTime: "8 min read",
    category: "Comparison",
  },
  {
    slug: "echonour-vs-otter",
    title: "Echonour vs Otter.ai: Which Transcription Tool Is Right for You?",
    description: "An honest comparison of Echonour and Otter.ai — features, pricing, accuracy, and use cases. Find out which tool fits your transcription needs.",
    date: "2026-02-23",
    readTime: "8 min read",
    category: "Comparison",
  },
  {
    slug: "echonour-vs-descript",
    title: "Echonour vs Descript: Which Transcription Tool Is Right for You?",
    description: "A detailed comparison of Echonour and Descript — two different approaches to transcription. One is a focused transcription tool, the other a full video editing suite.",
    date: "2026-02-23",
    readTime: "7 min read",
    category: "Comparison",
  },
  {
    slug: "how-to-transcribe-youtube-video",
    title: "How to Transcribe a YouTube Video (With Timestamps)",
    description: "Learn how to get accurate transcripts from any YouTube video, complete with timestamps and speaker identification. No software download required.",
    date: "2026-02-23",
    readTime: "5 min read",
    category: "Tutorial",
  },
  {
    slug: "how-to-transcribe-podcast-episode",
    title: "How to Transcribe a Podcast Episode in Minutes",
    description: "Turn any podcast into searchable text. Works with RSS feeds, direct audio URLs, and uploaded files. Get speaker names automatically identified.",
    date: "2026-02-23",
    readTime: "4 min read",
    category: "Tutorial",
  },
  {
    slug: "how-to-get-timestamps-in-transcription",
    title: "Getting Timestamps in Your Transcriptions: A Complete Guide",
    description: "Why timestamps matter for citations, navigation, and accessibility. Learn how to get precise timestamps in every transcript.",
    date: "2026-02-23",
    readTime: "4 min read",
    category: "Guide",
  },
  {
    slug: "how-to-identify-speakers-in-transcription",
    title: "Automatic Speaker Identification: How AI Names Your Speakers",
    description: "Discover how AI-powered speaker diarization identifies who said what, and how Echonour goes further by naming speakers automatically.",
    date: "2026-02-23",
    readTime: "5 min read",
    category: "Guide",
  },
];

// JSON-LD for blog listing
const BLOG_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Blog",
  "name": "Echonour Blog",
  "description": "Tutorials, guides, and tips for audio and video transcription",
  "url": "https://echonour.com/blog",
  "publisher": {
    "@type": "Organization",
    "name": "Echonour",
    "url": "https://echonour.com"
  },
  "blogPost": BLOG_POSTS.map(post => ({
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.description,
    "datePublished": post.date,
    "url": `https://echonour.com/blog/${post.slug}`
  }))
};

export default function BlogPage() {
  useEffect(() => {
    document.title = "Blog - Echonour";
    
    // Inject JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(BLOG_JSONLD);
    script.id = 'blog-jsonld';
    document.head.appendChild(script);
    
    return () => {
      const existing = document.getElementById('blog-jsonld');
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="min-h-[calc(100vh-68px)] bg-gray-50 dark:bg-gray-900">
      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
            Blog
          </h1>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
            Tutorials, guides, and tips for getting the most out of your transcriptions
          </p>
        </div>

        {/* Blog Posts Grid */}
        <div className="space-y-8">
          {BLOG_POSTS.map((post) => (
            <article
              key={post.slug}
              className="rounded-xl border border-gray-200 bg-white p-6 transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-800"
            >
              <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                  {post.category}
                </span>
                <span>{post.date}</span>
                <span>·</span>
                <span>{post.readTime}</span>
              </div>
              <Link to={`/blog/${post.slug}`}>
                <h2 className="mt-3 text-xl font-semibold text-gray-900 hover:text-blue-600 dark:text-white dark:hover:text-blue-400">
                  {post.title}
                </h2>
              </Link>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                {post.description}
              </p>
              <Link
                to={`/blog/${post.slug}`}
                className="mt-4 inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Read more
                <svg
                  className="ml-1 h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Link>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}

export { BLOG_POSTS };
